import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, User, Shield, Upload, Mail, Bell, CheckCircle2, X } from 'lucide-react';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';

export default function Admin() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ username: '', email: '', full_name: '', password: '', role: 'technician' });
  const [saving, setSaving] = useState(false);
  const [showSeed, setShowSeed] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState('');
  // CSV import state
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Email settings
  const [emailSettings, setEmailSettings] = useState({ emails: [], enabled: true });
  const [emailInput, setEmailInput] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/users').then(r => setUsers(r.data)).finally(() => setLoading(false));
    api.get('/settings/email').then(r => setEmailSettings(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditUser(null);
    setForm({ username: '', email: '', full_name: '', password: '', role: 'technician' });
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({ username: user.username, email: user.email || '', full_name: user.full_name || '', password: '', role: user.role });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...form };
      if (!data.password) delete data.password;
      if (editUser) {
        await api.put(`/admin/users/${editUser.id}`, data);
      } else {
        await api.post('/admin/users', data);
      }
      setShowModal(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (id === currentUser?.id) return;
    if (!window.confirm('¿Eliminar este usuario?')) return;
    await api.delete(`/admin/users/${id}`);
    load();
  };

  const handleSeed = async () => {
    setSeeding(true);
    setSeedMsg('');
    try {
      const res = await api.post('/seed');
      setSeedMsg(`Datos cargados: ${res.data.mobile_equipment} equipos móviles, ${res.data.fixed_equipment} fijos, ${res.data.inventory_items} repuestos.`);
    } catch (err) {
      setSeedMsg('Error: ' + (err.response?.data?.detail || 'Error desconocido'));
    } finally {
      setSeeding(false);
    }
  };

  const handleCsvImport = async () => {
    if (!csvFile) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', csvFile);
      const res = await api.post('/hourometer/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
    } catch (err) {
      setImportResult({ error: err.response?.data?.detail || 'Error en la importación' });
    } finally { setImporting(false); }
  };

  const handleSaveEmailSettings = async () => {
    setSavingEmail(true); setEmailMsg('');
    try {
      await api.put('/settings/email', emailSettings);
      setEmailMsg('Configuración guardada correctamente');
      setTimeout(() => setEmailMsg(''), 3000);
    } catch { setEmailMsg('Error guardando configuración'); }
    finally { setSavingEmail(false); }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true); setEmailMsg('');
    try {
      await api.post('/settings/email/test');
      setEmailMsg('Email de prueba enviado. Revisa la bandeja de entrada de los destinatarios configurados.');
    } catch (err) { setEmailMsg('Error: ' + (err.response?.data?.detail || 'No se pudo enviar')); }
    finally { setTestingEmail(false); }
  };

  const addEmail = () => {
    const e = emailInput.trim();
    if (e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) && !emailSettings.emails.includes(e)) {
      setEmailSettings(s => ({ ...s, emails: [...s.emails, e] }));
      setEmailInput('');
    }
  };

  const removeEmail = (em) => setEmailSettings(s => ({ ...s, emails: s.emails.filter(e => e !== em) }));

  return (
    <div className="p-6 md:p-8 page-enter space-y-6">
      <div className="mb-2">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Panel de Administración</h1>
        <p className="text-sm text-gray-500 mt-0.5">Gestión de usuarios y configuración del sistema</p>
      </div>

      {/* Seed Data Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-blue-900 text-sm mb-1">Cargar Datos del Excel</h3>
            <p className="text-xs text-blue-700">
              Carga todos los equipos del Ecoparque (22 móviles + 21 fijos), datos históricos 2023 y 2026, alertas y órdenes de trabajo de ejemplo.
              <br /><strong>Atención:</strong> Esta acción sobrescribe todos los datos actuales.
            </p>
            {seedMsg && <p className={`text-xs mt-2 font-medium ${seedMsg.startsWith('Error') ? 'text-red-600' : 'text-green-700'}`}>{seedMsg}</p>}
          </div>
          <button onClick={handleSeed} disabled={seeding} className="flex-shrink-0 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors" data-testid="seed-btn">
            {seeding ? 'Cargando...' : 'Cargar Datos Excel'}
          </button>
        </div>
      </div>

      {/* CSV Import for Hourometer */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <h2 className="font-semibold text-gray-900 text-sm mb-1" style={{ fontFamily: 'Barlow, sans-serif' }}>Importar Horómetro Masivo</h2>
        <p className="text-xs text-gray-500 mb-3">Sube un CSV o Excel con columnas: <code className="bg-gray-100 px-1 rounded">equipment_code, year, month, hours, km, diesel, notes</code></p>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <Upload size={14} className="text-gray-500" />
            <span className="text-gray-700">{csvFile ? csvFile.name : 'Seleccionar archivo (.csv / .xlsx)'}</span>
            <input type="file" className="hidden" accept=".csv,.xlsx,.xls" onChange={e => { setCsvFile(e.target.files[0]); setImportResult(null); }} />
          </label>
          {csvFile && (
            <button onClick={handleCsvImport} disabled={importing} className="px-4 py-2 bg-[#16a34a] text-white text-sm rounded-lg hover:bg-[#15803d]" data-testid="import-csv-btn">
              {importing ? 'Importando...' : 'Importar'}
            </button>
          )}
        </div>
        {importResult && (
          <div className={`mt-3 p-3 rounded-lg text-xs ${importResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-800'}`} data-testid="import-result">
            {importResult.error ? `Error: ${importResult.error}` : (
              <div>
                <p className="font-bold mb-1">Importación completada: {importResult.inserted} registros insertados / actualizados (de {importResult.total_rows} filas)</p>
                {importResult.errors?.length > 0 && (
                  <div className="mt-1 text-red-600">
                    <p className="font-medium">Errores ({importResult.errors.length}):</p>
                    {importResult.errors.slice(0, 5).map((e, i) => <p key={i}>{e}</p>)}
                    {importResult.errors.length > 5 && <p>...y {importResult.errors.length - 5} más</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Email Notifications */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-gray-900 text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow, sans-serif' }}><Mail size={15} className="text-[#16a34a]" />Notificaciones por Email</h2>
            <p className="text-xs text-gray-500 mt-0.5">Se envían por Mailchimp Transactional cuando se genera una nueva alerta de mantenimiento</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Activado</span>
            <button onClick={() => setEmailSettings(s => ({ ...s, enabled: !s.enabled }))} className={`relative w-10 h-5 rounded-full transition-colors ${emailSettings.enabled ? 'bg-[#16a34a]' : 'bg-gray-200'}`} data-testid="toggle-email-notifications">
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${emailSettings.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Destinatarios de alertas</label>
            <div className="flex gap-2">
              <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addEmail())} placeholder="email@empresa.es" className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" />
              <button type="button" onClick={addEmail} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200" data-testid="add-email-btn">Añadir</button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {emailSettings.emails.map(em => (
                <span key={em} className="flex items-center gap-1.5 text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2.5 py-1">
                  {em}<button onClick={() => removeEmail(em)} className="hover:text-red-500" data-testid={`remove-email-${em}`}><X size={11} /></button>
                </span>
              ))}
              {emailSettings.emails.length === 0 && <p className="text-xs text-gray-400">Sin destinatarios — los emails de alerta irán a todos los administradores con email registrado</p>}
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            <strong>Importante:</strong> Para enviar emails debes verificar tu dominio en <a href="https://mandrillapp.com" target="_blank" rel="noreferrer" className="underline">Mailchimp Transactional</a> y configurar el campo <code className="bg-amber-100 px-1 rounded">ALERT_EMAIL_FROM</code> en el archivo <code className="bg-amber-100 px-1 rounded">.env</code> del backend.
          </div>
          {emailMsg && <p className={`text-xs font-medium ${emailMsg.includes('Error') ? 'text-red-600' : 'text-green-700'}`}>{emailMsg}</p>}
          <div className="flex gap-2 pt-1">
            <button onClick={handleTestEmail} disabled={testingEmail} className="flex items-center gap-2 px-3 py-2 text-xs border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="test-email-btn">
              <Bell size={13} />{testingEmail ? 'Enviando...' : 'Enviar email de prueba'}
            </button>
            <button onClick={handleSaveEmailSettings} disabled={savingEmail} className="flex items-center gap-2 px-4 py-2 text-xs bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d]" data-testid="save-email-settings-btn">
              <CheckCircle2 size={13} />{savingEmail ? 'Guardando...' : 'Guardar configuración'}
            </button>
          </div>
        </div>
      </div>

      {/* Users Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900 text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>Usuarios del Sistema</h2>
          <button onClick={openCreate} className="flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-xs font-medium px-3 py-1.5 rounded-lg" data-testid="create-user-btn">
            <Plus size={14} /> Nuevo Usuario
          </button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase">Usuario</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase">Email</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase">Rol</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase">Estado</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-400">Cargando...</td></tr>
            ) : users.map(user => (
              <tr key={user.id} className="table-row-hover" data-testid={`user-row-${user.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#16a34a] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {(user.full_name || user.username || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{user.full_name || user.username}</p>
                      <p className="text-xs text-gray-400">@{user.username}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 text-sm text-gray-600">{user.email || '—'}</td>
                <td className="px-5 py-3">
                  <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full w-fit ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                    {user.role === 'admin' ? <Shield size={11} /> : <User size={11} />}
                    {user.role === 'admin' ? 'Admin' : 'Técnico'}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${user.active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {user.active !== false ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(user)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-user-${user.id}`}><Edit2 size={14} /></button>
                    {user.id !== currentUser?.id && (
                      <button onClick={() => handleDelete(user.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`delete-user-${user.id}`}><Trash2 size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>{editUser ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {[
                { label: 'Usuario', key: 'username', required: !editUser, disabled: !!editUser },
                { label: 'Nombre completo', key: 'full_name' },
                { label: 'Email', key: 'email', type: 'email' },
                { label: editUser ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña', key: 'password', type: 'password', required: !editUser },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{f.label}</label>
                  <input type={f.type || 'text'} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} required={f.required} disabled={f.disabled} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none disabled:bg-gray-50" />
                </div>
              ))}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Rol</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white">
                  <option value="technician">Técnico</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d]" data-testid="save-user-btn">
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

